import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, EventEmitter, Component, ViewEncapsulation, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import * as i5 from '@tinymce/tinymce-angular';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import * as i2 from '@yourcause/common/core-forms';
import { FormControlComponent, CoreFormsModule } from '@yourcause/common/core-forms';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i6 from '@yourcause/common/word-counter';
import { WordCounterModule } from '@yourcause/common/word-counter';

/*
* MAKE SURE THE GC API HAS THE LATEST VERSION OF THIS STYLESHEET
*/
/* tip: switch vscode language mode to css to make easier to work with */
/* THIS IS CSS NOT SCSS */
const editorStyles = `
.tox-editor-header {
  z-index: inherit !important;
}

.editor-body p {
  margin: 0;
}

@media print {
  .editor-body {
    box-shadow: none;
    border: none;
    margin: 0;
  }
}
.editor-body td, .editor-body th {
  font-size: 0.9em;
}
.editor-body h1 {
  margin-bottom: 1cm;
}
.editor-body table {
  margin-top: 0.5cm;
  margin-bottom: 0.5cm;
}
.editor-body table td {
  border-right: 1px solid #d0d0d0;
  border-bottom: 1px solid #d0d0d0;
}
.editor-body table td:first-of-type {
  border-left: 1px solid #d0d0d0;
}
.editor-body table tr:first-of-type td {
  border-top: 1px solid #d0d0d0;
}
.editor-body table thead th {
  border-top: 1px solid #d0d0d0;
  border-right: 1px solid #d0d0d0;
}
.editor-body table thead th:first-of-type {
  border-left: 1px solid #d0d0d0;
}
`;
const previewStyles = `
  body p {
    margin: 0;
  }

  @media print {
     body {
      box-shadow: none;
      border: none;
      margin: 0;
     }
  }

  body td,
  body th {
    font-size: 0.9em;
  }

  body h1 {
    margin-bottom: 1cm;
  }

  body table {
    margin-top: 0.5cm;
    margin-bottom: 0.5cm;
  }

  body table td {
    border-right: 1px solid #d0d0d0;
    border-bottom: 1px solid #d0d0d0;
  }

  body table td:first-of-type {
    border-left: 1px solid #d0d0d0;
  }

  body table tr:first-of-type td {
    border-top: 1px solid #d0d0d0;
  }

  body table thead th {
    border-top: 1px solid #d0d0d0;
    border-right: 1px solid #d0d0d0;
  }

  body table thead th:first-of-type {
    border-left: 1px solid #d0d0d0;
  }`;
function richTextEditorStyles (formatOptions, isEditorView, richTextSettings) {
    const stylesToAppend = isEditorView ? editorStyles : previewStyles;
    const openSansImport = `@import url("https://fonts.googleapis.com/css?family=Open+Sans:200,200i,400,400i,600,600i,700,700i");`;
    const nunitoSansImport = `@import url("https://fonts.googleapis.com/css?family=Nunito+Sans:200,200i,400,400i,600,600i,700,700i");`;
    let paddingTop = '1rem';
    let paddingBottom = '1rem';
    let paddingRight = '1rem';
    let paddingLeft = '1rem';
    let fontSize = richTextSettings.fontSize || '13px';
    let fontFamily = `"${richTextSettings.fontFamily || 'Open Sans'}"`;
    let fontColor = richTextSettings.fontColor || '13px';
    let lineHeight = richTextSettings.lineHeight || 1.4;
    if (!!formatOptions) {
        paddingTop = `${formatOptions.top}in`;
        paddingRight = `${formatOptions.right}in`;
        paddingBottom = `${formatOptions.bottom}in`;
        paddingLeft = `${formatOptions.left}in`;
    }
    let styles = openSansImport +
        nunitoSansImport +
        stylesToAppend +
        `body, editor-body {
      padding-top: ${paddingTop};
      padding-bottom: ${paddingBottom};
      padding-right: ${paddingRight};
      padding-left: ${paddingLeft};
      font-size: ${fontSize};
      font-family: ${fontFamily};
      color: ${fontColor};
      line-height: ${lineHeight};
    }`;
    return styles;
}

const Default_Font_Size = '13px';
const Default_Font_Color = '#000000';
const Default_Font_Family = 'Open Sans';
const Default_Line_Height = 1.4;
const RICH_TEXT_EDITOR_SERVICE = new InjectionToken('RichTextEditorService');
var InlineStyle;
(function (InlineStyle) {
    InlineStyle[InlineStyle["Input"] = 1] = "Input";
    InlineStyle[InlineStyle["Header_Block"] = 2] = "Header_Block";
})(InlineStyle || (InlineStyle = {}));

class RichTextEditorService {
    constructor(richTextEditorService) {
        this.richTextEditorService = richTextEditorService;
    }
    getRichTextEditorDefaults() {
        if (!!this.richTextEditorService) {
            return this.richTextEditorService.getRichTextEditorDefaults();
        }
        return {
            fontColor: Default_Font_Color,
            fontFamily: Default_Font_Family,
            fontSize: Default_Font_Size,
            lineHeight: Default_Line_Height
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorService, deps: [{ token: RICH_TEXT_EDITOR_SERVICE, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [RICH_TEXT_EDITOR_SERVICE]
                }, {
                    type: Optional
                }] }] });

class RichTextEditorComponent extends FormControlComponent {
    constructor(formGroupDirective, formControlService, richTextEditorService) {
        super(formGroupDirective, formControlService);
        this.richTextEditorService = richTextEditorService;
        this.plugins = 'preview importcss searchreplace autolink save directionality code visualblocks visualchars fullscreen image link table pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help';
        this.menubar = 'file edit view insert format tools table help';
        this.toolbar = 'undo redo | link image | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor | pagebreak | fullscreen  preview print';
        this.height = 300;
        this.tokenInsertionOnly = false;
        this.tokenStart = '{{';
        this.tokenEnd = '}}';
        this.isInline = false;
        this.inlineStyle = InlineStyle.Input;
        this.stripHtml = false;
        this.fileUploadEnabled = false;
        this.tokenToInsert = '';
        this.showWordCount = false;
        this.showCharCount = false;
        this.showMaxLength = false;
        this.adjustHeightWhenDisabled = false;
        this.onEditorBlur = new EventEmitter();
        this.InlineStyle = InlineStyle;
        this.showEditor = true;
        this.isAfterInit = false;
        this.imageUploadHandler = async (blobInfo) => {
            const blob = blobInfo.blob();
            const fileName = blobInfo.filename();
            const payload = {
                blob,
                fileName
            };
            return this.uploadFileFunc(payload);
        };
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.isInline && this.tokenInsertionOnly) {
            this.height = undefined;
            this.plugins = '';
            this.toolbar = '';
            this.menubar = 'false';
        }
        this.setOptions();
        this.isAfterInit = true;
    }
    ngOnChanges(changes) {
        if (this.isAfterInit) {
            if (changes.tokenToInsert && !!this.tokenToInsert) {
                // Parent component triggering token insert
                this.insertToken();
            }
            else if (changes.plugins ||
                changes.menubar ||
                changes.toolbar ||
                changes.height ||
                changes.templateMargins) {
                // We need to completely reset the editor if these configurations change after init
                this.resetEditor();
            }
        }
    }
    insertToken() {
        if (this.tokenToInsert) {
            const editor = this.editorElement.editor;
            if (editor) {
                const token = `${this.tokenStart}${this.tokenToInsert}${this.tokenEnd}`;
                if (!this.isInline) {
                    editor.execCommand('mceInsertContent', false, token);
                }
                else {
                    editor.setContent(`${this.value} ${token}`);
                }
            }
        }
    }
    resetEditor() {
        this.showEditor = false;
        // Force editor to completely reset with *ngIf="showEditor" in template
        setTimeout(() => {
            this.showEditor = true;
            this.ngOnInit();
        });
    }
    onEditorChange() {
        // Wait for form group to be updated
        setTimeout(() => {
            let value = this.value;
            // Strip HTML if necessary and emit
            if (this.stripHtml) {
                // Value has already been emitted, but now we have to emit the adpated value
                value = this.doStripHtml(value);
                super._onChange(value);
            }
        });
    }
    doStripHtml(data) {
        return data.replace(/<\/?[^>]+(>|$)/g, '');
    }
    editorBlur() {
        this.onEditorBlur.emit();
    }
    setOptions() {
        const isDisabled = this.control.disabled;
        if (isDisabled) {
            this.toolbar = '';
            this.plugins = '';
            if (this.adjustHeightWhenDisabled && !this._value) {
                this.height = 100;
            }
        }
        const styles = richTextEditorStyles(this.templateMargins, true, this.richTextEditorService.getRichTextEditorDefaults());
        const options = {
            plugins: this.plugins,
            inline: this.isInline,
            height: this.height,
            promotion: false,
            branding: false,
            menubar: this.menubar,
            toolbar: this.toolbar,
            toolbar_mode: 'wrap',
            file_picker_types: 'image',
            body_class: 'editor-body',
            content_style: styles,
            font_family_formats: 'Arial=arial,helvetica,sans-serif; Courier New=courier new,courier; Georgia=georgia; Nunito Sans=Nunito Sans; Open Sans=open sans, sans-serif; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geeva; Verdana=verdana,geneva;',
            font_size_formats: '10px 12px 13px 14px 15px 16px 17px 18px 24px 36px 48px',
            convert_urls: false,
            a11y_advanced_options: true,
            highlight_on_focus: true,
            help_accessibility: true,
            paste_as_text: true
        };
        if (this.fileUploadEnabled) {
            options.image_uploadtab = true;
            options.images_upload_handler = this.imageUploadHandler;
        }
        this.options = options;
    }
    ngOnDestroy() {
        const editor = this.editorElement.editor;
        if (editor) {
            editor.destroy();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorComponent, deps: [{ token: i1.FormGroupDirective }, { token: i2.FormControlService }, { token: RichTextEditorService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: RichTextEditorComponent, selector: "yc-rich-text-editor", inputs: { plugins: "plugins", menubar: "menubar", toolbar: "toolbar", height: "height", tokenInsertionOnly: "tokenInsertionOnly", tokenStart: "tokenStart", tokenEnd: "tokenEnd", isInline: "isInline", inlineStyle: "inlineStyle", stripHtml: "stripHtml", templateMargins: "templateMargins", fileUploadEnabled: "fileUploadEnabled", tokenToInsert: "tokenToInsert", showWordCount: "showWordCount", showCharCount: "showCharCount", showMaxLength: "showMaxLength", maxLength: "maxLength", adjustHeightWhenDisabled: "adjustHeightWhenDisabled", uploadFileFunc: "uploadFileFunc" }, outputs: { onEditorBlur: "onEditorBlur" }, viewQueries: [{ propertyName: "editorElement", first: true, predicate: ["editorElement"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"showEditor\"\n  class=\"form-group\"\n  [class.mb-0]=\"noMarginBottom\"\n  [class.ng-invalid]=\"!!(error || customError) && isDirty\">\n\n  <yc-form-label\n    [srOnly]=\"srOnlyLabel\"\n    [name]=\"name\"\n    [label]=\"label\"\n    [description]=\"description\"\n    [tooltipText]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [showQuestionMarkForTooltip]=\"showQuestionMarkForTooltip\"\n    [asteriskForRequired]=\"addRequiredAsterisk\"\n    [customIcon]=\"customLabelIcon\"\n    [customIconTooltip]=\"customLabelIconTooltip\"\n    [customIconClass]=\"customLabelIconClass\">\n  </yc-form-label>\n\n  <div class=\"form-input-wrapper\"\n    [class.yc-input-icon-left]=\"leftIcon\"\n    [class.yc-input-icon-right]=\"rightIcons?.length ? rightIcons[0] : null\">\n\n    <!-- Rich Text Editor -->\n    <editor #editorElement\n      [class.form-control]=\"isInline && inlineStyle === InlineStyle.Input\"\n      [class.inline-input]=\"isInline && inlineStyle === InlineStyle.Input\"\n      [class.header-block]=\"isInline && inlineStyle === InlineStyle.Header_Block\"\n      [init]=\"options\"\n      [licenseKey]=\"'gpl'\"\n      [formControl]=\"$any(control)\"\n      (onChange)=\"onEditorChange()\"\n      (onBlur)=\"editorBlur()\">\n    </editor>\n\n    <!-- Word Counter -->\n    <yc-word-counter *ngIf=\"showWordCount || showCharCount\"\n      class=\"d-flex flex-grow-1 justify-content-end\"\n      [showWordCount]=\"showWordCount\"\n      [showCharCount]=\"showCharCount\"\n      [maxLength]=\"maxLength\"\n      [showMaxLength]=\"showMaxLength\"\n      [inputValue]=\"_value\">\n    </yc-word-counter>\n  </div>\n\n  <ng-content></ng-content>\n\n  <yc-form-help-or-error\n    [error]=\"error\"\n    [customError]=\"customError\"\n    [showError]=\"showError\"\n    [showHelp]=\"showHelp\"\n    [help]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help-or-error>\n</div>", styles: [".inline-input{height:36px!important;font-weight:400;font-family:Open Sans,Helvetica Neue,Helvetica,Arial,sans-serif;font-size:.875rem}.header-block{padding:5px;border-bottom:1px dashed #ced4da;font-size:1.5rem;font-weight:600;color:#fff}.mce-edit-focus{outline:none!important}\n"], dependencies: [{ kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.FormLabelComponent, selector: "yc-form-label", inputs: ["name", "label", "srOnly", "description", "tabIndex", "cursorStyle", "tooltipText", "popoverTemplate", "showQuestionMarkForTooltip", "asteriskForRequired", "forRadioButtons", "customIcon", "customIconTooltip", "customIconClass", "forFileUpload", "triggerUpload"] }, { kind: "component", type: i2.FormHelpOrErrorComponent, selector: "yc-form-help-or-error", inputs: ["error", "customError", "showError", "showHelp", "help", "helpTextClass"] }, { kind: "component", type: i5.EditorComponent, selector: "editor", inputs: ["cloudChannel", "apiKey", "licenseKey", "init", "id", "initialValue", "outputFormat", "inline", "tagName", "plugins", "toolbar", "modelEvents", "allowedEvents", "ignoreEvents", "disabled"] }, { kind: "component", type: i6.WordCounterComponent, selector: "yc-word-counter", inputs: ["showWordCount", "showCharCount", "showMaxLength", "maxLength", "inputValue"] }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'yc-rich-text-editor', encapsulation: ViewEncapsulation.None, template: "<div *ngIf=\"showEditor\"\n  class=\"form-group\"\n  [class.mb-0]=\"noMarginBottom\"\n  [class.ng-invalid]=\"!!(error || customError) && isDirty\">\n\n  <yc-form-label\n    [srOnly]=\"srOnlyLabel\"\n    [name]=\"name\"\n    [label]=\"label\"\n    [description]=\"description\"\n    [tooltipText]=\"tooltipText\"\n    [popoverTemplate]=\"popoverTemplate\"\n    [showQuestionMarkForTooltip]=\"showQuestionMarkForTooltip\"\n    [asteriskForRequired]=\"addRequiredAsterisk\"\n    [customIcon]=\"customLabelIcon\"\n    [customIconTooltip]=\"customLabelIconTooltip\"\n    [customIconClass]=\"customLabelIconClass\">\n  </yc-form-label>\n\n  <div class=\"form-input-wrapper\"\n    [class.yc-input-icon-left]=\"leftIcon\"\n    [class.yc-input-icon-right]=\"rightIcons?.length ? rightIcons[0] : null\">\n\n    <!-- Rich Text Editor -->\n    <editor #editorElement\n      [class.form-control]=\"isInline && inlineStyle === InlineStyle.Input\"\n      [class.inline-input]=\"isInline && inlineStyle === InlineStyle.Input\"\n      [class.header-block]=\"isInline && inlineStyle === InlineStyle.Header_Block\"\n      [init]=\"options\"\n      [licenseKey]=\"'gpl'\"\n      [formControl]=\"$any(control)\"\n      (onChange)=\"onEditorChange()\"\n      (onBlur)=\"editorBlur()\">\n    </editor>\n\n    <!-- Word Counter -->\n    <yc-word-counter *ngIf=\"showWordCount || showCharCount\"\n      class=\"d-flex flex-grow-1 justify-content-end\"\n      [showWordCount]=\"showWordCount\"\n      [showCharCount]=\"showCharCount\"\n      [maxLength]=\"maxLength\"\n      [showMaxLength]=\"showMaxLength\"\n      [inputValue]=\"_value\">\n    </yc-word-counter>\n  </div>\n\n  <ng-content></ng-content>\n\n  <yc-form-help-or-error\n    [error]=\"error\"\n    [customError]=\"customError\"\n    [showError]=\"showError\"\n    [showHelp]=\"showHelp\"\n    [help]=\"help\"\n    [helpTextClass]=\"helpTextClass\">\n  </yc-form-help-or-error>\n</div>", styles: [".inline-input{height:36px!important;font-weight:400;font-family:Open Sans,Helvetica Neue,Helvetica,Arial,sans-serif;font-size:.875rem}.header-block{padding:5px;border-bottom:1px dashed #ced4da;font-size:1.5rem;font-weight:600;color:#fff}.mce-edit-focus{outline:none!important}\n"] }]
        }], ctorParameters: () => [{ type: i1.FormGroupDirective }, { type: i2.FormControlService }, { type: RichTextEditorService }], propDecorators: { plugins: [{
                type: Input
            }], menubar: [{
                type: Input
            }], toolbar: [{
                type: Input
            }], height: [{
                type: Input
            }], tokenInsertionOnly: [{
                type: Input
            }], tokenStart: [{
                type: Input
            }], tokenEnd: [{
                type: Input
            }], isInline: [{
                type: Input
            }], inlineStyle: [{
                type: Input
            }], stripHtml: [{
                type: Input
            }], templateMargins: [{
                type: Input
            }], fileUploadEnabled: [{
                type: Input
            }], tokenToInsert: [{
                type: Input
            }], showWordCount: [{
                type: Input
            }], showCharCount: [{
                type: Input
            }], showMaxLength: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], adjustHeightWhenDisabled: [{
                type: Input
            }], uploadFileFunc: [{
                type: Input
            }], onEditorBlur: [{
                type: Output
            }], editorElement: [{
                type: ViewChild,
                args: ['editorElement']
            }] } });

class RichTextEditorModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorModule, declarations: [RichTextEditorComponent], imports: [CommonModule,
            CoreFormsModule,
            EditorModule,
            WordCounterModule,
            ReactiveFormsModule], exports: [RichTextEditorComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorModule, providers: [
            {
                provide: TINYMCE_SCRIPT_SRC,
                useValue: 'tinymce/tinymce.min.js'
            }
        ], imports: [CommonModule,
            CoreFormsModule,
            EditorModule,
            WordCounterModule,
            ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RichTextEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CoreFormsModule,
                        EditorModule,
                        WordCounterModule,
                        ReactiveFormsModule
                    ],
                    providers: [
                        {
                            provide: TINYMCE_SCRIPT_SRC,
                            useValue: 'tinymce/tinymce.min.js'
                        }
                    ],
                    declarations: [
                        RichTextEditorComponent
                    ],
                    exports: [
                        RichTextEditorComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Default_Font_Color, Default_Font_Family, Default_Font_Size, Default_Line_Height, InlineStyle, RICH_TEXT_EDITOR_SERVICE, RichTextEditorComponent, RichTextEditorModule, RichTextEditorService, richTextEditorStyles };
